import { useEffect, useState } from 'react';
import { BeakerIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { geneticsApi, type PairPredictionResult } from '../../api/genetics';
import { subscribeGeneticsCatalogChanged } from '../../utils/geneticsCatalogEvents';
import { GENDER_ICON_FEMALE, GENDER_ICON_MALE } from '../../utils/genderDisplay';

interface BreedingPairGeneticsPanelProps {
  breedingPairId: string;
  maleBirdId: string;
  femaleBirdId: string;
  speciesId?: string;
}

export default function BreedingPairGeneticsPanel({
  breedingPairId,
  maleBirdId,
  femaleBirdId,
  speciesId,
}: BreedingPairGeneticsPanelProps) {
  const [prediction, setPrediction] = useState<PairPredictionResult | null>(null);
  const [pairCoi, setPairCoi] = useState<{ percent: number; message?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPrediction = async () => {
    setLoading(true);
    setError(null);
    try {
      if (speciesId) {
        await geneticsApi.seedSpecies(speciesId);
      }
      const result = await geneticsApi.predictBreedingPair(breedingPairId);
      setPrediction(result);
      const coi = await geneticsApi.getPairCoi(maleBirdId, femaleBirdId);
      setPairCoi({ percent: coi.percent, message: coi.message });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load genetics prediction');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (breedingPairId && maleBirdId && femaleBirdId) {
      void loadPrediction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breedingPairId, maleBirdId, femaleBirdId, speciesId]);

  useEffect(
    () => subscribeGeneticsCatalogChanged(speciesId, () => void loadPrediction()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [speciesId, breedingPairId, maleBirdId, femaleBirdId],
  );

  return (
    <div className="mt-6 rounded-lg border border-indigo-200 bg-indigo-50/50 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
          <BeakerIcon className="h-5 w-5" />
          Offspring Genetics Prediction
        </h4>
        <button
          type="button"
          onClick={loadPrediction}
          disabled={loading}
          className="text-xs text-indigo-700 hover:text-indigo-900 underline disabled:opacity-50"
        >
          {loading ? 'Calculating…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p className="text-sm text-red-600 mb-2">{error}</p>
      )}

      {pairCoi ? (
        <p className="text-xs text-slate-700 bg-white border border-indigo-100 rounded px-3 py-2 mb-3">
          Predicted offspring COI: <span className="font-medium">{pairCoi.percent}%</span>
          {pairCoi.message ? ` — ${pairCoi.message}` : null}
        </p>
      ) : null}

      {!loading && !prediction && !error && (
        <p className="text-sm text-slate-600">No prediction available.</p>
      )}

      {prediction?.warnings?.length ? (
        <div className="mb-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
          <div className="flex items-start gap-1">
            <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <ul className="list-disc list-inside space-y-0.5">
              {prediction.warnings.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {prediction?.loci?.length ? (
        <div className="space-y-4">
          {prediction.loci
            .filter((locus) => {
              // Hide catalog loci where both parents are wild and offspring are 100% wild
              // (GenCalc only lists loci present in the genetic code).
              const onlyWild =
                locus.outcomes.length === 1 &&
                locus.outcomes[0].probability >= 0.999 &&
                !locus.outcomes[0].isVisual &&
                !locus.outcomes[0].isSplit &&
                locus.outcomes[0].phenotype === 'Wild type';
              const parentsWild =
                locus.parentMale?.phenotype === 'Wild type' &&
                locus.parentFemale?.phenotype === 'Wild type';
              return !(onlyWild && parentsWild);
            })
            .map((locus) => (
            <div key={locus.locusSymbol} className="bg-white rounded-md border border-indigo-100 p-3">
              <div className="flex items-baseline justify-between mb-2">
                <span className="font-medium text-slate-900">
                  {locus.locusName} <span className="text-slate-500 font-mono text-xs">({locus.locusSymbol})</span>
                </span>
                <span className="text-xs text-slate-500">{locus.inheritanceMode.replace(/_/g, ' ')}</span>
              </div>
              {(locus.parentMale || locus.parentFemale) && (
                <p className="text-[11px] text-slate-500 mb-2">
                  Parents:{' '}
                  <span className="font-mono">
                    {GENDER_ICON_MALE} {locus.parentMale?.phenotype ?? '?'} ({locus.parentMale?.genotype ?? '?'})
                  </span>
                  {' · '}
                  <span className="font-mono">
                    {GENDER_ICON_FEMALE} {locus.parentFemale?.phenotype ?? '?'} ({locus.parentFemale?.genotype ?? '?'})
                  </span>
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                {locus.outcomes.slice(0, 8).map((o) => (
                  <div key={`${o.genotype}-${o.sex}-${o.phenotype}`} className="text-xs flex justify-between gap-2 py-1 border-b border-slate-50">
                    <span>
                      {o.phenotype}
                      {o.sex !== 'EITHER' && <span className="text-slate-400"> ({o.sex === 'MALE' ? GENDER_ICON_MALE : GENDER_ICON_FEMALE})</span>}
                      <span className="text-slate-400 font-mono ml-1">{o.genotype}</span>
                    </span>
                    <span className="font-medium text-indigo-700">{(o.probability * 100).toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : !loading ? (
        <p className="text-sm text-slate-600">
          Record structured genotypes on parent birds, or seed a species mutation catalog to enable predictions.
        </p>
      ) : null}
    </div>
  );
}
